import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { AsyncContentMui } from "components";
import queryString from "query-string";
import {
  useDealActions,
  useDefaultAccountBookmarksSelector,
  useIgnoreDefaultView,
  usePortfolioConstructionActions,
  useReportsActions,
  useSelectBookmarksAll,
  useUserActions,
  useUserRoleSelector
} from "store";
import { IBookmark, IBookmarkType, IReportType, ReportsQueries, UrlQueryParams } from "store/types";
import { reportStateFromQueries } from "utils/reports-state-from-queries";

import { RoleName } from "../../constants";

interface Props {
  children: React.ReactNode;
  reportType: IBookmark["report_type"];
  ignoreCurrency?: boolean; // Deal report should be in deal currency
  bookmarkType: IBookmarkType;
}

export const checkReportType = (persistedReportType: string | null, reportType: IReportType | null): boolean => {
  if (persistedReportType === reportType) {
    return true;
  }

  // Backward compatibility
  if (reportType === IReportType.PORTFOLIO) {
    return (
      persistedReportType === "portfolio_overview" ||
      persistedReportType === "timeline" ||
      persistedReportType === "classic"
    );
  }

  return false;
};

export const DefaultViewGuard: React.FC<Props> = ({ reportType, children, ignoreCurrency, bookmarkType }) => {
  const [isContent, isIsContent] = useState<boolean>(false);
  const role = useUserRoleSelector();
  const { replace } = useHistory();

  const bookmarks = useSelectBookmarksAll();
  const ignoreDefaultView = useIgnoreDefaultView();

  const { replaceQueriesAction, setIgnoreDefaultView, setQuickViewQueriesAction } = useReportsActions();
  const { setCorrelationMatrixSettingsAction } = usePortfolioConstructionActions();
  const { setSelectedBookmark } = useUserActions();
  const { updateQueries } = useDealActions();
  const {
    deal: defaultAccountDealBookmark,
    quick_view: defaultAccountQuickViewBookmark,
    report: defaultAccountReportBookmark,
    investor_view: defaultAccountInvestorViewBookmark,
    correlation_matrix: defaultAccountCorrelationMatrixBookmark,
    data_operations: defaultAccountDataOperationsBookmark
  } = useDefaultAccountBookmarksSelector();

  const ignoreDefaultViewRef = useRef<boolean>(ignoreDefaultView);

  const handleReport = useCallback((): number | undefined => {
    const defaultBookmark = bookmarks.find(
      (item) => item.type === bookmarkType && item.is_default && checkReportType(item.report_type, reportType)
    );

    const defaultAccountBookmark = defaultAccountReportBookmark?.find(
      (item) => item.report_type === reportType && checkReportType(item.report_type, reportType)
    );

    let queries: Partial<ReportsQueries<string>> = {};

    if (!defaultBookmark && !defaultAccountBookmark) return;

    queries = reportStateFromQueries(defaultBookmark?.params || defaultAccountBookmark?.params || "") || {};
    defaultBookmark && setSelectedBookmark(defaultBookmark.id);
    reportType && replaceQueriesAction({ queries, options: { ignoreCurrency } });

    return defaultBookmark?.id;
  }, [bookmarks, bookmarkType, replaceQueriesAction, defaultAccountReportBookmark]);

  const handleMyDeals = useCallback((): number | undefined => {
    //investor_view is bookmark type for myDeals (dashboard only)
    const defaultBookmark = bookmarks.find(
      (item) =>
        (item.type === bookmarkType && item.is_default) ||
        //user can assign investor_view bookmark as default for oneself
        (bookmarkType === "deal" && item.type === "investor_view" && item.is_default)
    );

    if (!defaultBookmark && !defaultAccountDealBookmark && !defaultAccountInvestorViewBookmark) return;
    //if we don't have default bookmark, we use default account bookmark
    const { deals: dealsFromAPI }: UrlQueryParams = queryString.parseUrl(
      defaultBookmark?.params ||
        //if user is investor, we use default account dashboard investor view bookmark
        (role?.name === RoleName.Investor ? defaultAccountInvestorViewBookmark : defaultAccountDealBookmark) ||
        ""
    ).query;

    if (dealsFromAPI && !Array.isArray(dealsFromAPI)) {
      updateQueries(JSON.parse(dealsFromAPI), true);
    }

    return defaultBookmark?.id;
  }, [bookmarks, updateQueries]);

  const handleQuickView = useCallback((): number | undefined => {
    const defaultBookmark = bookmarks.find((item) => item.type === bookmarkType && item.is_default);

    if (!defaultBookmark && !defaultAccountQuickViewBookmark) return;
    //if we don't have default bookmark, we use default account bookmark
    const { quickViewQueries }: UrlQueryParams = queryString.parseUrl(
      defaultBookmark?.params || defaultAccountQuickViewBookmark || ""
    ).query;
    const queries = quickViewQueries ? JSON.parse(quickViewQueries) : null;
    if (queries) {
      setQuickViewQueriesAction({ queries, replace: true });
    }
    return defaultBookmark?.id;
  }, [bookmarks]);

  const handleCorrelationMatrix = useCallback((): number | undefined => {
    const defaultBookmark = bookmarks.find((item) => item.type === bookmarkType && item.is_default);

    if (!defaultBookmark && !defaultAccountCorrelationMatrixBookmark) return;

    const { correlationMatrixSettings }: UrlQueryParams = queryString.parseUrl(
      defaultBookmark?.params || defaultAccountCorrelationMatrixBookmark || ""
    ).query;

    const queries = correlationMatrixSettings ? JSON.parse(correlationMatrixSettings) : null;

    if (correlationMatrixSettings) {
      setCorrelationMatrixSettingsAction(queries);
    }

    return defaultBookmark?.id;
  }, [bookmarks]);

  const handleDataOperations = useCallback((): number | undefined => {
    const defaultBookmark = bookmarks.find((item) => item.type === bookmarkType && item.is_default);

    if (!defaultBookmark && !defaultAccountDataOperationsBookmark) return;

    const bookmarkParams = defaultBookmark?.params || defaultAccountDataOperationsBookmark || "";
    if (!bookmarkParams) return defaultBookmark?.id;

    const [bookmarkPath, bookmarkSearch = ""] = bookmarkParams.split("?");
    replace({
      pathname: bookmarkPath,
      search: bookmarkSearch
    });

    return defaultBookmark?.id;
  }, [bookmarks]);

  useEffect(() => {
    ignoreDefaultViewRef.current = ignoreDefaultView;
    if (ignoreDefaultView) {
      setIgnoreDefaultView(false);
    }
  }, [ignoreDefaultView]);

  useEffect(() => {
    if (
      !ignoreDefaultView ||
      defaultAccountReportBookmark ||
      defaultAccountDealBookmark ||
      defaultAccountQuickViewBookmark ||
      defaultAccountInvestorViewBookmark ||
      defaultAccountCorrelationMatrixBookmark ||
      defaultAccountDataOperationsBookmark
    ) {
      let defaultBookmarkId: number | undefined;
      if (bookmarkType === "report") {
        defaultBookmarkId = handleReport();
      }
      if (bookmarkType === "deal") {
        defaultBookmarkId = handleMyDeals();
      }

      if (bookmarkType === "quick_view") {
        defaultBookmarkId = handleQuickView();
      }

      if (bookmarkType === "correlation_matrix") {
        defaultBookmarkId = handleCorrelationMatrix();
      }

      if (bookmarkType === "data_operations") {
        defaultBookmarkId = handleDataOperations();
      }

      if (defaultBookmarkId) {
        setSelectedBookmark(defaultBookmarkId);
      }
    }
    setTimeout(() => isIsContent(true), 0); // Open page when store is updated - not good
  }, []);

  useEffect(() => {
    return function clearBookmark() {
      if (!ignoreDefaultViewRef.current) {
        setSelectedBookmark(null);
      }
    };
  }, []);

  return (
    <AsyncContentMui isLoading={!isContent} spinnerProps={{ size: 150 }}>
      {children}
    </AsyncContentMui>
  );
};
